import { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { displayFlooredOrCeiledNumber } from 'utils/misc';

import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { Box, Chip, lighten } from '@mui/material';
import { GRAY, GRAY_LIGHT, ORANGE_SECONDARY } from 'constants/colors';

type Props = {
  maxHours: number;
  currentMaxHours: number;
  savedInSDB: number;
  savedInSDBPreview: number;
  savedInSalarySystem: number;
  savedInSalarySystemPreview: number;
  remainingHours: number;
  showSavedHoursOnly?: boolean;
};

export default function TotalBudgetHours(props: Props) {
  const {
    maxHours,
    currentMaxHours,
    savedInSDB,
    savedInSDBPreview,
    savedInSalarySystem,
    savedInSalarySystemPreview,
    remainingHours
  } = props;
  const { t } = useTranslation('common');

  const [showSavedInSalarySystemHours, setShowSavedInSalarySystemHours] =
    useState<boolean>(false);

  return (
    <Paper className="total-budget">
      <h2>
        {t('balanceBudget.totalBudgetHours')}:{' '}
        <big
          className={classNames('semibold', {
            primary:
              savedInSDBPreview > 0 ||
              (showSavedInSalarySystemHours && savedInSalarySystemPreview > 0)
          })}
        >
          {displayFlooredOrCeiledNumber(
            savedInSDB +
              savedInSDBPreview +
              (showSavedInSalarySystemHours ? savedInSalarySystem : 0)
          )}
        </big>
      </h2>
      <div className="bar d-flex">
        <Tooltip
          title={`${t('balanceBudget.depositedSDBPlanning')}: ${t(
            'amountHours',
            { hours: displayFlooredOrCeiledNumber(savedInSDB) }
          )}`}
          arrow
        >
          <div
            className="sdb"
            style={{
              width: `${
                ((savedInSDB - savedInSalarySystemPreview) / currentMaxHours) *
                100
              }%`
            }}
          />
        </Tooltip>
        {savedInSDBPreview > 0 && (
          <Tooltip
            title={`${t('preview')}: ${t('amountHours', {
              hours: displayFlooredOrCeiledNumber(savedInSDBPreview)
            })}`}
            arrow
          >
            <div
              className="sdb preview"
              style={{
                width: `${(savedInSDBPreview / currentMaxHours) * 100}%`
              }}
            />
          </Tooltip>
        )}
        <Tooltip
          title={`${t('balanceBudget.savedSalarySystem')}: ${t('amountHours', {
            hours: displayFlooredOrCeiledNumber(savedInSalarySystem)
          })}`}
          arrow
        >
          <div
            className="system"
            style={{
              width: `${
                (showSavedInSalarySystemHours
                  ? savedInSalarySystem / currentMaxHours
                  : 0) * 100
              }%`
            }}
          />
        </Tooltip>
        {showSavedInSalarySystemHours && savedInSalarySystemPreview > 0 && (
          <Tooltip
            title={`${t('preview')}: ${t('amountHours', {
              hours: displayFlooredOrCeiledNumber(savedInSalarySystemPreview)
            })}`}
            arrow
          >
            <div
              className="system preview"
              style={{
                width: `${
                  (showSavedInSalarySystemHours
                    ? savedInSalarySystemPreview / currentMaxHours
                    : 0) * 100
                }%`
              }}
            />
          </Tooltip>
        )}
        <Tooltip
          title={`${t('balanceBudget.remainingHours', {
            hours: displayFlooredOrCeiledNumber(remainingHours)
          })}`}
          arrow
        >
          <div className="grow-1" />
        </Tooltip>
        <div className="out-of">
          {t('outOfHours', {
            hours: displayFlooredOrCeiledNumber(currentMaxHours)
          })}
        </div>
        <div className="max">
          {t('maxHours', { hours: displayFlooredOrCeiledNumber(maxHours) })}
        </div>
      </div>
      <Box display="flex" flexDirection="column" gap={1} mt={3}>
        <Chip
          label={`
            ${t('balanceBudget.savedSalarySystem')} -
            ${t('amountHours', {
              hours: displayFlooredOrCeiledNumber(savedInSalarySystem)
            })}
          `}
          size="small"
          variant="outlined"
          sx={{
            width: 'fit-content',
            backgroundColor: showSavedInSalarySystemHours
              ? lighten(ORANGE_SECONDARY, 0.3)
              : GRAY_LIGHT,
            borderColor: showSavedInSalarySystemHours ? ORANGE_SECONDARY : GRAY,
            '&.MuiChip-clickable:hover': {
              backgroundColor: ORANGE_SECONDARY,
              borderColor: ORANGE_SECONDARY
            }
          }}
          onClick={setShowSavedInSalarySystemHours.bind(
            null,
            !showSavedInSalarySystemHours
          )}
        />
      </Box>
    </Paper>
  );
}
